import { useCallback, useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Checkbox,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import InfoIcon from "@mui/icons-material/Info";

import MaskAndValid from "utils/maskAndValid";

import {
  getDefaultCondicaoPagamentoId,
  resolveLineDiscount,
  resolveLineEncargo,
  netUnitPrice,
} from "../utils/paymentConditionUtils";
import PaymentConditionSelect from "./PaymentConditionSelect";
import GradeMatrix, { adjustQtyToMultiple } from "./GradeMatrix";
import {
  getGradeCell,
  listGradeAgrupadores,
  listGradeSizes,
} from "../../shared/utils/family-grade";

function BulkFilterDropdown({
  label,
  options,
  selected,
  onToggle,
  open,
  setOpen,
  theme,
}) {
  const wrapperRef = useRef(null);

  // Fecha ao clicar fora do dropdown (botão + lista) — sem isso, o menu só fechava
  // reabrindo outro filtro ou clicando numa opção, ficava aberto clicando em
  // qualquer outro lugar do modal.
  useEffect(() => {
    if (!open) return undefined;
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, setOpen]);

  return (
    <Box className="custom-select-wrapper" ref={wrapperRef}>
      <Box
        className="custom-select-btn"
        onClick={() => setOpen(!open)}
        style={{
          borderColor: open ? theme.primary : undefined,
          boxShadow: open ? `0 0 0 1px ${theme.primary}` : undefined,
        }}
      >
        <Typography>{label}</Typography>
        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      {open && (
        <Box className="custom-select-list">
          {options.map((option) => (
            <Box
              key={option.id}
              className={`custom-select-item ${
                selected.has(option.id) ? "selected" : ""
              }`}
              onClick={() => onToggle(option.id)}
              style={
                selected.has(option.id)
                  ? { backgroundColor: "#eff6ff", color: theme.primary }
                  : undefined
              }
            >
              <Checkbox
                checked={selected.has(option.id)}
                className="b2b-checkbox"
                style={{ color: theme.primary }}
              />
              <Typography>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

function bulkFilterLabel(selected, emptyLabel, singularSuffix, pluralSuffix) {
  if (selected.size === 0) return emptyLabel;
  if (selected.size === 1) return Array.from(selected)[0];
  return `${selected.size} ${pluralSuffix || singularSuffix}`;
}

export default function GridModal({
  open,
  onClose,
  onSave,
  currentBrand,
  activeFamilyKey,
  families,
  tempGridState,
  setTempGridState,
  selectedBulkAgrupadores,
  selectedBulkSizes,
  toggleBulkSelection,
  theme,
}) {
  const [bulkQty, setBulkQty] = useState("");
  const [bulkPaymentConditionId, setBulkPaymentConditionId] = useState(null);
  const [subDropdownOpen, setSubDropdownOpen] = useState(false);
  const [sizeDropdownOpen, setSizeDropdownOpen] = useState(false);
  const [paymentDropdownOpen, setPaymentDropdownOpen] = useState(false);
  const sessionKeyRef = useRef(null);

  const fam =
    activeFamilyKey && families[currentBrand]
      ? families[currentBrand][activeFamilyKey]
      : null;

  useEffect(() => {
    if (!open) {
      sessionKeyRef.current = null;
      return;
    }
    if (!activeFamilyKey) return;

    const currentFam = families[currentBrand]?.[activeFamilyKey];
    if (!currentFam) return;

    const sessionKey = `${currentBrand}:${activeFamilyKey}`;
    if (sessionKeyRef.current === sessionKey) return;
    sessionKeyRef.current = sessionKey;

    // Simulação sempre parte da condição padrão da vitrine — a condição real
    // agora é escolhida no carrinho, por vitrine, não existe mais um histórico
    // "condição já usada nos itens do carrinho" para herdar aqui.
    setBulkPaymentConditionId(
      getDefaultCondicaoPagamentoId(
        currentFam.condicoesPagamento || [],
        currentFam.idCondicaoPagamentoPadrao,
      ),
    );
  }, [open, activeFamilyKey, currentBrand, families]);

  // Hooks precisam vir antes de qualquer early return (regras dos Hooks), por isso
  // são null-safe: quando fam é null o modal não renderiza a grade e estes callbacks
  // não chegam a ser executados.
  const defaultCondicaoId = fam?.idCondicaoPagamentoPadrao;

  // useCallback com deps [fam, condição]: mantém a identidade estável enquanto o
  // usuário digita quantidades (tempGridState), evitando recomputar o cabeçalho da
  // grade a cada tecla. Só muda quando a família ou a condição de simulação muda.
  const discountForProduct = useCallback(
    (product) =>
      resolveLineDiscount(
        product,
        fam,
        bulkPaymentConditionId ?? defaultCondicaoId,
      ),
    [fam, bulkPaymentConditionId, defaultCondicaoId],
  );

  // Encargo não depende do produto (não é configurável por item/vitrine, só pela condição) nem da
  // simulação de bulkPaymentConditionId separadamente — reaproveita a mesma condição resolvida acima.
  const encargoForProduct = useCallback(
    () => resolveLineEncargo(fam, bulkPaymentConditionId ?? defaultCondicaoId),
    [fam, bulkPaymentConditionId, defaultCondicaoId],
  );

  // Updater funcional + useCallback: identidade estável para que as células
  // (InputCell memoizadas) só re-renderizem quando SEU valor muda, não a cada tecla.
  const handleUpdateQty = useCallback(
    (compoundId, rawValue, multiple) => {
      let qty = parseInt(rawValue, 10);
      setTempGridState((prev) => {
        const nextGrid = { ...prev };
        if (qty > 0) {
          qty = adjustQtyToMultiple(qty, multiple);
          if (qty <= 0) {
            delete nextGrid[compoundId];
          } else {
            nextGrid[compoundId] = qty;
          }
        } else {
          delete nextGrid[compoundId];
        }
        return nextGrid;
      });
    },
    [setTempGridState],
  );

  // Total simulado da grade (soma qty × preço com encargo+desconto de cada célula preenchida) —
  // hook precisa vir antes do early return abaixo (regras dos Hooks), por isso null-safe.
  const gridTotal = useMemo(() => {
    if (!fam) return 0;
    const famSizes = listGradeSizes(fam);
    let total = 0;
    (fam.grade?.groups || []).forEach((group) => {
      famSizes.forEach((tamanho) => {
        const product = getGradeCell(fam, group.linha, tamanho);
        if (!product) return;
        const compoundId = `${activeFamilyKey}_${product.id}`;
        const qty = tempGridState[compoundId];
        if (!qty) return;
        const finalPrice = netUnitPrice(
          product,
          discountForProduct(product),
          encargoForProduct(product),
        );
        total += finalPrice * qty;
      });
    });
    return total;
  }, [fam, activeFamilyKey, tempGridState, discountForProduct, encargoForProduct]);

  if (!activeFamilyKey || !fam) return null;

  const condicoesPagamento = fam.condicoesPagamento || [];
  const sizes = listGradeSizes(fam);

  const handleBulkPaymentChange = (id) => {
    const paymentId = Number(id);
    if (!paymentId || Number.isNaN(paymentId)) return;
    setBulkPaymentConditionId(paymentId);
  };

  const handleApplyBulk = () => {
    const qty = parseInt(bulkQty, 10);
    if (Number.isNaN(qty) || qty < 0) return;

    setSubDropdownOpen(false);
    setSizeDropdownOpen(false);
    setPaymentDropdownOpen(false);

    const nextGrid = { ...tempGridState };

    (fam.grade?.groups || []).forEach((group) => {
      if (
        selectedBulkAgrupadores.size > 0 &&
        !selectedBulkAgrupadores.has(group.linha)
      ) {
        return;
      }

      sizes.forEach((tamanho) => {
        if (selectedBulkSizes.size > 0 && !selectedBulkSizes.has(tamanho)) {
          return;
        }

        const product = getGradeCell(fam, group.linha, tamanho);
        if (!product) return;

        const compoundId = `${activeFamilyKey}_${product.id}`;

        const adjustedQty = adjustQtyToMultiple(qty, product.multiple || 1);
        if (adjustedQty > 0) {
          nextGrid[compoundId] = adjustedQty;
        } else {
          delete nextGrid[compoundId];
        }
      });
    });

    setTempGridState(nextGrid);
    setBulkQty("");
  };

  const handleConfirmSave = () => {
    const finalGrid = { ...tempGridState };

    Object.entries(finalGrid).forEach(([key, qty]) => {
      if (qty == null || Number(qty) <= 0) {
        delete finalGrid[key];
      }
    });

    onSave(finalGrid);
  };

  const subOptions = listGradeAgrupadores(fam).map((sub) => ({
    id: sub,
    label: sub,
  }));
  const sizeOptions = sizes.map((tamanho) => ({
    id: tamanho,
    label: tamanho,
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      className="grid-modal-dialog"
      PaperProps={{ className: "grid-modal-paper" }}
    >
      <DialogTitle className="grid-modal-header">
        <Box className="grid-modal-header-left">
          <Box>
            <Typography
              className="grid-modal-title brand-font"
              style={{ color: theme.primary }}
            >
              {fam.productDisplayName || fam.name}
            </Typography>
            <Typography className="grid-modal-subtitle">
              {condicoesPagamento.length > 0
                ? "Informe a quantidade dos itens e selecione uma condição de pagamento para simular preços"
                : "Selecione as quantidades na grade"}
            </Typography>
          </Box>
          <Button onClick={onClose} className="close-btn">
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>

      <Box className="grid-modal-toolbar-stack">
        <Box className="grid-modal-toolbar grid-modal-toolbar-massa">
          <Typography
            className="toolbar-label"
            style={{ color: theme.primary }}
          >
            Quantidade:
          </Typography>

          <Box className="bulk-qty-input-wrapper">
            <Typography className="bulk-qty-label">Qtde</Typography>
            <TextField
              type="number"
              value={bulkQty}
              onChange={(e) => setBulkQty(e.target.value)}
              placeholder="0"
              className="bulk-qty-input"
              variant="outlined"
              inputProps={{ style: { height: "100%", width: "80px" } }}
              style={{
                border: "none",
                background: "none",
                height: "100%",
                width: "80px",
              }}
            />
          </Box>

          <Box className="toolbar-divider" />

          <BulkFilterDropdown
            label={bulkFilterLabel(
              selectedBulkAgrupadores,
              "Todos os itens",
              "item",
              "itens",
            )}
            options={subOptions}
            selected={selectedBulkAgrupadores}
            onToggle={(value) => toggleBulkSelection("agrupador", value)}
            open={subDropdownOpen}
            setOpen={setSubDropdownOpen}
            theme={theme}
          />

          <BulkFilterDropdown
            label={bulkFilterLabel(
              selectedBulkSizes,
              "Todos os tamanhos",
              "tamanho",
              "tamanhos",
            )}
            options={sizeOptions}
            selected={selectedBulkSizes}
            onToggle={(value) => toggleBulkSelection("size", value)}
            open={sizeDropdownOpen}
            setOpen={setSizeDropdownOpen}
            theme={theme}
          />

          <Button
            className="apply-bulk-btn"
            onClick={handleApplyBulk}
            style={{ backgroundColor: theme.primary }}
          >
            Aplicar
          </Button>

          <Box className="toolbar-info toolbar-info-massa">
            <InfoIcon className="info-icon" style={{ color: theme.primary }} />
            <Typography className="info-text">
              O botão Aplicar altera <strong>quantidade</strong> conforme o
              filtro e tamanhos dos itens acima.
            </Typography>
          </Box>
        </Box>

        {condicoesPagamento.length > 0 && (
          <Box className="grid-modal-toolbar grid-modal-toolbar-payment">
            <Typography
              className="toolbar-label"
              style={{ color: theme.primary }}
            >
              Condição de pagamento (simulação de preço):
            </Typography>
            <PaymentConditionSelect
              condicoes={condicoesPagamento}
              value={bulkPaymentConditionId}
              onChange={handleBulkPaymentChange}
              defaultId={defaultCondicaoId}
              theme={theme}
              open={paymentDropdownOpen}
              setOpen={setPaymentDropdownOpen}
            />
            <Box className="toolbar-info toolbar-info-payment">
              <InfoIcon
                className="info-icon"
                style={{ color: theme.primary }}
              />
              <Typography className="info-text">
                Usada aqui apenas para <strong>simular o preço</strong>. A
                condição de pagamento definitiva é escolhida no carrinho e vale
                para todos os itens da mesma vitrine.
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      <DialogContent className="grid-modal-content">
        <GradeMatrix
          fam={fam}
          familyKey={activeFamilyKey}
          theme={theme}
          tempGridState={tempGridState}
          onUpdateQty={handleUpdateQty}
          discountForProduct={discountForProduct}
          encargoForProduct={encargoForProduct}
        />
      </DialogContent>

      <DialogActions className="grid-modal-actions">
        <Typography
          className="grid-modal-total"
          variant="h3"
          style={{ color: theme.primary, fontWeight: 700, marginRight: "auto" }}
        >
          Total: R$ {MaskAndValid.maskDecimal(gridTotal, 2)}
        </Typography>
        <Box className="grid-modal-actions-buttons">
          <Button
            onClick={handleConfirmSave}
            className="save-btn"
            style={{ backgroundColor: theme.primary }}
            startIcon={<SaveIcon />}
          >
            Confirmar
          </Button>
          <Button onClick={onClose} className="cancel-btn">
            Cancelar
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
